import React, { createContext, useContext, useMemo } from 'react';
import { AnalyticsDataContext } from './AnalyticsDataContext';
import { VolumeShocker } from '../types';

interface VolumeShockerContextValue {
  volumeShockerSymbols: Set<string>;
}

const VolumeShockerContext = createContext<VolumeShockerContextValue>({
  volumeShockerSymbols: new Set<string>(),
});

const buildVolumeShockerSet = (volumeShockers?: VolumeShocker[] | null) =>
  new Set((volumeShockers ?? []).map((item) => item.Symbol).filter(Boolean));

interface VolumeShockerProviderProps {
  children: React.ReactNode;
  symbols?: Iterable<string>;
}

export const VolumeShockerProvider: React.FC<VolumeShockerProviderProps> = ({ children, symbols }) => {
  const analyticsCtx = useContext(AnalyticsDataContext);

  const volumeShockerSymbols = useMemo(() => {
    if (symbols !== undefined) {
      return new Set(Array.from(symbols).filter(Boolean));
    }
    const activeShockers = analyticsCtx?.selectedData?.VolumeShockers ?? analyticsCtx?.data[0]?.VolumeShockers;
    return buildVolumeShockerSet(activeShockers);
  }, [symbols, analyticsCtx?.selectedData?.VolumeShockers, analyticsCtx?.data]);

  const value = useMemo(
    () => ({
      volumeShockerSymbols,
    }),
    [volumeShockerSymbols]
  );

  return <VolumeShockerContext.Provider value={value}>{children}</VolumeShockerContext.Provider>;
};

export const useVolumeShockerSymbols = () => useContext(VolumeShockerContext);

export const getVolumeShockerSymbols = (volumeShockers?: VolumeShocker[] | null) =>
  buildVolumeShockerSet(volumeShockers);